import { useState, useEffect, useCallback } from 'react';

import { cfetch } from '@confluence/network';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { getMonitoringClient } from '@confluence/monitoring/entry-points/getMonitoringClient.ts';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';

import { fetchShortcutEnabledState } from './preloadShortcutEnabledState.ts';

let inflightPromise: Promise<boolean> | null = null;

const updaters = new Set<(enabled: boolean) => void>();

export const useKeyboardShortcutsEnabled = (): [boolean, (enabled: boolean) => void] => {
	const { isLoggedIn, isLicensed } = useSessionData();
	const [enabled, setEnabled] = useState<boolean>(window.__SHORTCUT_ENABLED__ ?? false);

	useEffect(() => {
		if (typeof window.__SHORTCUT_ENABLED__ !== 'boolean') {
			if (!inflightPromise) {
				inflightPromise =
					// preloadShortcutEnabledState will set window.__SHORTCUT_ENABLED__
					isLoggedIn && isLicensed ? fetchShortcutEnabledState() : Promise.resolve(false);
			}
			void inflightPromise.then(setEnabled);
		}

		updaters.add(setEnabled);

		return () => {
			updaters.delete(setEnabled);
		};
	}, [isLoggedIn, isLicensed]);

	const notify = useCallback(async (enabled: boolean) => {
		try {
			const res = await cfetch('/wiki/rest/confluenceshortcuts/latest/enabled', {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({ enabled }),
			});

			if (res.ok) {
				const newEnabled = (await res.text()) === 'true';
				// Any newly created hook instance will get the updated value
				window.__SHORTCUT_ENABLED__ = newEnabled;
				// Notify all the existing hooks on the page that we have updated the value
				updaters.forEach((updater) => updater(newEnabled));
			}
		} catch (e) {
			getMonitoringClient().submitError(
				new Error(`Error updating keyboard shortcuts enabled state: ${e.message}`),
				{ attribution: Attribution.BACKBONE },
			);
		}
	}, []);

	return [Boolean(enabled), notify];
};
